import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { COLORS, FONTS, device } from '../../constants';
import { SelectArrow, SelectCross } from '../../assets/svg';
import { SelectList } from './SelectList/SelectList';
import { Text } from '../Text/Text';
import { SHADOW } from '../../constants/animations';

const initialValue = 'All';

interface SelectProps {
  optionsList: string[];
  isMultiple?: boolean;
  onChange?: (selected: string[]) => void;
  selectedOptions?: string[];
  searchEnabled?: boolean;
  listMaxHeight?: string;
}

export const Select = ({
  optionsList,
  isMultiple = false,
  onChange,
  selectedOptions: externalSelectedOptions,
  searchEnabled = false,
  listMaxHeight,
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [internalSelectedOptions, setInternalSelectedOptions] = useState<
    string[]
  >(isMultiple ? [initialValue] : [optionsList[0]]);
  const ignoreNextClick = useRef(false);

  const selectedOptions = externalSelectedOptions || internalSelectedOptions;

  const handleSelectClose = () => {
    ignoreNextClick.current = true;
    setIsOpen(false);
  };

  const handleOptionSelect = (option: string) => {
    if (isMultiple) {
      let newSelection: string[];

      if (option === initialValue) {
        newSelection = [initialValue];
      } else {
        if (selectedOptions.includes(initialValue)) {
          newSelection = [option];
        } else {
          if (selectedOptions.includes(option)) {
            newSelection = selectedOptions.filter(item => item !== option);
          } else {
            newSelection = [...selectedOptions, option];
          }
        }
      }

      onChange?.(newSelection);
      if (!externalSelectedOptions) {
        setInternalSelectedOptions(newSelection);
      }
    } else {
      const newSelection = [option];
      onChange?.(newSelection);
      if (!externalSelectedOptions) {
        setInternalSelectedOptions(newSelection);
      }
      setIsOpen(false);
    }
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMultiple) {
      const newSelection = [initialValue];
      onChange?.(newSelection);
      if (!externalSelectedOptions) {
        setInternalSelectedOptions(newSelection);
      }
    }
  };

  const handleOpenSelect = () => {
    if (ignoreNextClick.current) {
      ignoreNextClick.current = false;
      return;
    }
    setIsOpen(true);
  };

  const displayText = isMultiple
    ? selectedOptions.length > 0
      ? selectedOptions.includes(initialValue)
        ? initialValue
        : selectedOptions.join(', ')
      : initialValue
    : selectedOptions[0];

  const showResetIcon =
    isMultiple &&
    !selectedOptions.includes(initialValue) &&
    selectedOptions.length > 0;
  const hasSelectedOptions =
    isMultiple &&
    !selectedOptions.includes(initialValue) &&
    selectedOptions.length > 0;

  return (
    <SelectContainer
      onClick={handleOpenSelect}
      $isOpen={isOpen}
      $hasSelectedOptions={hasSelectedOptions}
    >
      <SelectMain>
        <TextWrapper>
          <Text
            text={displayText}
            size={18}
            fontFamily={FONTS.SUBTITLE}
            color={COLORS.SECTION_TITLE}
          />
        </TextWrapper>
        <SelectIconContainer>
          {showResetIcon ? (
            <ResetIcon onClick={handleReset}>
              <SelectCross />
            </ResetIcon>
          ) : (
            <SelectIcon $isOpen={isOpen}>
              <SelectArrow />
            </SelectIcon>
          )}
        </SelectIconContainer>
      </SelectMain>
      <SelectList
        optionsList={optionsList}
        isOpen={isOpen}
        handleSelectClose={handleSelectClose}
        selectedOptions={selectedOptions}
        onOptionSelect={handleOptionSelect}
        isMultiple={isMultiple}
        enableSearch={searchEnabled}
        maxHeight={listMaxHeight}
        initialOption={initialValue}
      />
    </SelectContainer>
  );
};

const SelectContainer = styled.div<{
  $hasSelectedOptions: boolean;
  $isOpen: boolean;
}>`
    width: 165px;
    height: 52px;
    background-color: ${COLORS.WHITE};
    cursor: pointer;
    position: relative;
    box-sizing: border-box;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    border-color: ${({ $hasSelectedOptions }) =>
      $hasSelectedOptions ? COLORS.MAIN_RED : COLORS.INDUSTRY_BORDER};
    box-shadow: ${({ $isOpen }) => ($isOpen ? SHADOW.HOVER.FILTER : 'none')};
}
`;

const SelectMain = styled.div`
  width: 100%;
  height: 100%;
  border-width: 1px;
  padding: 10px;
  box-sizing: border-box;
  border-style: solid;
  border-color: ${COLORS.INDUSTRY_BORDER};
  display: flex;
  z-index: 12;
  align-items: center;
  justify-content: space-between;
`;

const SelectIconContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SelectIcon = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: inherit;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const ResetIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const TextWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  > h4 {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media screen and ${device.md} {
    > h4 {
      font-size: 14px;
    }
  }
`;
